import {
  IconCalendar,
  IconCar,
  IconId,
  IconMail,
  IconMapPin,
  IconPencil,
  IconPhone,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { Avatar } from "@mantine/core";
import { Input } from "antd";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Address {
  city: string;
  district: string;
  postalCode: number;
  address: string;
}

interface EmployeeData {
  name: string;
  gender: string;
  birthday: string;
  bloodType: string;
  nationalIdNo: string;
  motorbikeLicense: boolean;
  carLicense: boolean;
  address: Address;
  user?: {
    email: string;
  };
  profile: string | null;
  emergencyContact: string;
  emergencyContactRelationship: string;
  emergencyContactPhoneNumber: string;
  note: string;
}

function EmployeePage() {
  const { t } = useTranslation();
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    name,
    gender,
    birthday,
    bloodType,
    nationalIdNo,
    motorbikeLicense,
    carLicense,
    address,
    user,
    profile,
    emergencyContact,
    emergencyContactRelationship,
    emergencyContactPhoneNumber,
    note,
  } = employeeData || {};

  const {
    city = "",
    district = "",
    postalCode = 0,
    address: addressLine = "",
  } = address || {};

  const email = user?.email || "";

  const handleDisplayGender = (gender: string | undefined): string => {
    if (!gender) return t("employee.gender.unknown");
    if (gender === "male") {
      return t("employee.gender.male");
    } else if (gender === "female") {
      return t("employee.gender.female");
    } else {
      return t("employee.gender.unknown");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) return;
        const result = await single(orpcClient.employee.read([{ id: id! }]));
        setEmployeeData(result as any);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, [id]);

  return (
    <div className="flex h-full items-center justify-center p-14">
      <div className="w-[765px] rounded-xl bg-white p-16">
        <div className="flex justify-end">
          <IconPencil
            className="cursor-pointer"
            size={32}
            onClick={() => navigate(`/hrMainPage/editEmployee/${id}`)}
          />
        </div>
        <div className="flex">
          {profile ? (
            <img
              src={profile}
              style={{ maxWidth: "100px" }}
              className="w-24 h-24 object-cover rounded-full"
            />
          ) : (
            <Avatar size="xl" key={name} name={name} />
          )}
          <div className="p-4">
            <div className="text-3xl font-bold" style={{ color: "#6F6F6F" }}>
              {name}
            </div>
            <div className="" style={{ color: "#6F6F6F" }}>
              {handleDisplayGender(gender)}
            </div>
          </div>
        </div>
        <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("employee.basicInfo")}
        </div>
        <div className="flex justify-between py-4">
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.birthday")}</div>
            <div className="flex">
              <IconCalendar size={16} />
              <div className="pl-2">{dayjs(birthday).format("YYYY-MM-DD")}</div>
            </div>
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.bloodType")}</div>
            <div className="flex">
              <IconId size={16} />
              <div className="pl-2">{bloodType}型</div>
            </div>
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.idNumber")}</div>
            <div className="flex">
              <IconId size={16} />
              <div className="pl-2">{nationalIdNo}</div>
            </div>
          </div>
        </div>

        <div className="py-4">
          <div style={{ color: "#6F6F6F" }}>{t("employee.licenses")}</div>
          <div className="flex py-4">
            <div className=" px-4">
              <div style={{ color: "#6F6F6F" }}>{t("employee.motorcycle")}</div>
              <div className="flex">
                <IconCar size={16} />
                <div className="pl-2">
                  {motorbikeLicense == true
                    ? t("employee.has")
                    : t("employee.hasNot")}
                </div>
              </div>
            </div>
            <div className=" px-4">
              <div style={{ color: "#6F6F6F" }}>{t("employee.car")}</div>
              <div className="flex">
                <IconCar size={16} />
                <div className="pl-2">
                  {carLicense == true
                    ? t("employee.has")
                    : t("employee.hasNot")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("employee.contactAddress")}
        </div>
        <div className="flex justify-between py-4">
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.city")}</div>
            <div className="flex">
              <IconMapPin size={16} />
              <div className="pl-2">{city}</div>
            </div>
          </div>

          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.district")}</div>
            <div className="flex">
              <IconMapPin size={16} />
              <div className="pl-2">{district}</div>
            </div>
          </div>

          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.postalCode")}</div>
            <div className="flex">
              <IconMapPin size={16} />
              <div className="pl-2">{postalCode}</div>
            </div>
          </div>

          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.address")}</div>
            <div className="flex">
              <IconMapPin size={16} />
              <div className="pl-2">{addressLine}</div>
            </div>
          </div>
        </div>

        <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("employee.contactInfo")}
        </div>
        <div className="flex justify-start py-4">
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.email")}</div>
            <div className="flex">
              <IconMail size={16} />
              <div className="pl-2">{email}</div>
            </div>
          </div>

          {/* <div className="py-4 pl-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.phone")}</div>
            <div className="flex">
              <IconPhone size={16} />
              <div className="pl-2">{}</div>
            </div>
          </div> */}
        </div>
        <div className="flex justify-between py-4">
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("employee.emergencyContact")}
            </div>
            <div className="flex">
              <IconUser size={16} />
              <div className="pl-2">{emergencyContact}</div>
            </div>
          </div>
          <div className="py-4 pl-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.relationship")}</div>
            <div className="flex">
              <IconUser size={16} />
              <div className="pl-2">{emergencyContactRelationship}</div>
            </div>
          </div>

          <div className="py-4 pl-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("employee.emergencyPhone")}
            </div>
            <div className="flex">
              <IconPhone size={16} />
              <div className="pl-2">{emergencyContactPhoneNumber}</div>
            </div>
          </div>
        </div>
        <div className="py-4">
          <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("employee.notes")}
          </div>
          <Input.TextArea
            placeholder="Input placeholder"
            value={note}
            disabled={true}
          />
        </div>
      </div>
    </div>
  );
}

export default EmployeePage;
